import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, 
  Users, 
  FolderOpen, 
  FileText, 
  MessageSquare, 
  Upload, 
  Settings, 
  Activity,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  Globe,
  Wrench,
  UserCircle,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Code2,
  Bell,
  Search
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';

const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { language, toggleLanguage } = useLanguage();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const content = {
    ar: {
      dashboard: 'لوحة التحكم',
      profile: 'الملف الشخصي',
      users: 'المستخدمون',
      projects: 'المشاريع',
      posts: 'المقالات',
      chat: 'المحادثات',
      uploads: 'الملفات',
      settings: 'الإعدادات',
      activity: 'سجل النشاط',
      logout: 'تسجيل الخروج',
      maintenance: 'الصيانة',
      search: 'بحث...',
      adminPanel: 'لوحة التحكم'
    },
    en: {
      dashboard: 'Dashboard',
      profile: 'Profile',
      users: 'Users',
      projects: 'Projects',
      posts: 'Posts',
      chat: 'Chat',
      uploads: 'Uploads',
      settings: 'Settings',
      activity: 'Activity',
      logout: 'Logout',
      maintenance: 'Maintenance',
      search: 'Search...',
      adminPanel: 'Admin Panel'
    }
  };

  const t = content[language];

  const menuItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: t.dashboard, color: 'from-blue-500 to-cyan-500' },
    { path: '/admin/profile', icon: UserCircle, label: t.profile, color: 'from-purple-500 to-pink-500' },
    { path: '/admin/users', icon: Users, label: t.users, color: 'from-emerald-500 to-teal-500' },
    { path: '/admin/projects', icon: FolderOpen, label: t.projects, color: 'from-orange-500 to-amber-500' },
    { path: '/admin/posts', icon: FileText, label: t.posts, color: 'from-rose-500 to-pink-500' },
    { path: '/admin/chat', icon: MessageSquare, label: t.chat, color: 'from-violet-500 to-purple-500' },
    { path: '/admin/uploads', icon: Upload, label: t.uploads, color: 'from-sky-500 to-blue-500' },
    { path: '/admin/activity', icon: Activity, label: t.activity, color: 'from-lime-500 to-green-500' },
    { path: '/admin/maintenance', icon: Wrench, label: t.maintenance, color: 'from-amber-500 to-yellow-500' },
    { path: '/admin/settings', icon: Settings, label: t.settings, color: 'from-gray-500 to-slate-500' },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const isActive = (path) => location.pathname === path;

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className={`h-20 flex items-center ${isSidebarOpen ? 'justify-between px-6' : 'justify-center'} border-b border-white/10`}>
        {isSidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/25">
              <Code2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold text-white">{t.adminPanel}</span>
              <div className="flex items-center gap-1 text-xs text-gray-400">
                <Sparkles className="w-3 h-3" />
                <span>v2.0</span>
              </div>
            </div>
          </motion.div>
        )}
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-gray-400 transition-colors hidden md:flex"
        >
          {isSidebarOpen ? (
            <ChevronLeft className="w-5 h-5" />
          ) : (
            <ChevronRight className="w-5 h-5" />
          )}
        </motion.button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3">
        <div className="space-y-1">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            
            return (
              <motion.div
                key={item.path}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.03 }}
              >
                <Link
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`group relative flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                    active
                      ? 'bg-white/10 text-white'
                      : 'text-gray-400 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  {active && (
                    <motion.div
                      layoutId="sidebar-active"
                      className={`absolute inset-0 bg-gradient-to-r ${item.color} opacity-20 rounded-xl`}
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                  {active && (
                    <motion.div
                      layoutId="sidebar-indicator"
                      className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b ${item.color} rounded-full`}
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                  <div className={`relative p-2 rounded-lg transition-all duration-300 ${
                    active 
                      ? `bg-gradient-to-br ${item.color} shadow-lg` 
                      : 'bg-white/5 group-hover:bg-white/10'
                  }`}>
                    <Icon className={`w-5 h-5 ${active ? 'text-white' : ''}`} />
                  </div>
                  {isSidebarOpen && (
                    <span className="relative font-medium">{item.label}</span>
                  )}
                </Link>
              </motion.div>
            );
          })}
        </div>
      </nav>

      {/* Bottom Actions */}
      <div className="border-t border-white/10 p-3 space-y-1">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={toggleTheme}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-400 hover:bg-white/5 hover:text-white transition-all duration-300"
        >
          <div className="p-2 rounded-lg bg-white/5">
            {theme === 'dark' ? (
              <Sun className="w-5 h-5 text-amber-400" />
            ) : (
              <Moon className="w-5 h-5 text-indigo-400" />
            )}
          </div>
          {isSidebarOpen && (
            <span className="font-medium">
              {theme === 'dark' 
                ? (language === 'ar' ? 'وضع فاتح' : 'Light Mode')
                : (language === 'ar' ? 'وضع داكن' : 'Dark Mode')
              }
            </span>
          )}
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={toggleLanguage}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-400 hover:bg-white/5 hover:text-white transition-all duration-300"
        >
          <div className="p-2 rounded-lg bg-white/5">
            <Globe className="w-5 h-5" />
          </div>
          {isSidebarOpen && (
            <span className="font-medium">
              {language === 'ar' ? 'English' : 'عربي'}
            </span>
          )}
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10 transition-all duration-300"
        >
          <div className="p-2 rounded-lg bg-red-500/10">
            <LogOut className="w-5 h-5" />
          </div>
          {isSidebarOpen && (
            <span className="font-medium">{t.logout}</span>
          )}
        </motion.button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-slate-900">
      {/* Desktop Sidebar */}
      <motion.aside 
        initial={false}
        animate={{ width: isSidebarOpen ? 280 : 88 }}
        className="hidden md:flex flex-col bg-gradient-to-b from-slate-800 to-slate-900 border-r border-white/10"
      >
        <SidebarContent />
      </motion.aside>

      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileMenuOpen(true)}
        className="md:hidden fixed top-4 left-4 z-50 p-3 bg-slate-800 rounded-xl text-gray-400 border border-white/10"
      >
        <Menu className="w-6 h-6" />
      </button>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="md:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            />
            <motion.aside
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: "spring", damping: 25 }}
              className="md:hidden fixed left-0 top-0 bottom-0 w-72 bg-gradient-to-b from-slate-800 to-slate-900 border-r border-white/10 z-50 flex flex-col"
            >
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="absolute top-4 right-4 p-2 rounded-xl bg-white/5 text-gray-400"
              >
                <X className="w-5 h-5" />
              </button>
              <SidebarContent />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-20 bg-slate-800/50 backdrop-blur-xl border-b border-white/10 flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <div className="hidden md:block">
              <h2 className="text-xl font-bold text-white">
                {menuItems.find(item => isActive(item.path))?.label || t.dashboard}
              </h2>
              <p className="text-sm text-gray-400">
                {language === 'ar' ? 'مرحباً بك في لوحة التحكم' : 'Welcome to your dashboard'}
              </p>
            </div>
          </div>

          {/* Search Bar */}
          <div className="hidden lg:flex items-center flex-1 max-w-md mx-8">
            <div className="relative w-full">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                placeholder={t.search}
                className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 transition-colors"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Notifications */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="relative p-3 bg-white/5 hover:bg-white/10 rounded-xl text-gray-400 hover:text-white transition-colors"
            >
              <Bell className="w-5 h-5" />
              <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full" />
            </motion.button>

            {/* User Profile */}
            <Link 
              to="/admin/profile"
              className="flex items-center gap-3 p-2 pr-4 bg-white/5 hover:bg-white/10 rounded-xl transition-colors"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
                <span className="text-white font-bold">
                  {user?.displayName?.charAt(0) || 'A'}
                </span>
              </div>
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-white">
                  {user?.displayName || 'Admin'}
                </p>
                <p className="text-xs text-gray-400">
                  {user?.email}
                </p>
              </div>
            </Link>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto bg-gradient-to-br from-slate-900 via-slate-900 to-purple-900/20 p-6">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Outlet />
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
